import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  updateComplexPolymerEditingState,
  updateComplexPolymerVersion,
} from 'modbio/redux/ComplexPolymerReduxActions';
import {getStore} from 'redux-stores/Store';
import APIUtil from 'util/APIUtil';

import {
  ComplexPolymerCurrentVersionQuery,
  ComplexPolymerCurrentVersionQueryVariables,
} from './__generated/ComplexPolymerCurrentVersionQuery';
import {
  ComplexPolymerSchemaConfigurationQuery,
  ComplexPolymerSchemaConfigurationQueryVariables,
} from './__generated/ComplexPolymerSchemaConfigurationQuery';
import {
  ComplexPolymerTagSchemaQuery,
  ComplexPolymerTagSchemaQueryVariables,
} from './__generated/ComplexPolymerTagSchemaQuery';

type ComplexPolymerSchemaConfigurationGraphQL =
  ComplexPolymerSchemaConfigurationQuery['complexPolymerSchemaConfiguration'];

export interface ComplexPolymerStateData {
  complexPolymerSchemaConfiguration: ComplexPolymerSchemaConfigurationGraphQL | null;
}

export const COMPLEX_POLYMER_TAG_SCHEMA_QUERY = gql`
  query ComplexPolymerTagSchemaQuery($id: StrictString!) @skipAudit @engComponent(name: MODIFIED_BIOLOGICS) {
    folderItem(id: $id) {
      ... on FolderItemInterface {
        id
        tagSchema {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const COMPLEX_POLYMER_SCHEMA_CONFIGURATION_QUERY = gql`
  query ComplexPolymerSchemaConfigurationQuery($schemaId: StrictString!)
  @skipAudit
  @engComponent(name: MODIFIED_BIOLOGICS) {
    complexPolymerSchemaConfiguration(schemaId: $schemaId) {
      ... on OligoConjugateSchemaConfigNode {
        id
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const COMPLEX_POLYMER_CURRENT_VERSION_QUERY = gql`
  query ComplexPolymerCurrentVersionQuery($complexPolymerId: StrictString!)
  @skipAudit
  @engComponent(name: MODIFIED_BIOLOGICS) {
    complexPolymer(id: $complexPolymerId) {
      ... on RegistryItemInterface {
        id
      }
      ... on ComplexPolymerInterface {
        currentVersion {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export async function fetchComplexPolymerData(fileId: string): Promise<ComplexPolymerStateData | null> {
  if (!BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES) {
    return null;
  }

  if (!APIUtil.isBasicFolderItem(fileId)) {
    return null;
  }
  const schemaData = await doQuerySafe<ComplexPolymerTagSchemaQuery, ComplexPolymerTagSchemaQueryVariables>(
    {query: COMPLEX_POLYMER_TAG_SCHEMA_QUERY},
    {variables: {id: fileId}}
  );

  if (schemaData.folderItem.__typename !== 'BasicFolderItemNode' || schemaData.folderItem.tagSchema == null) {
    return null;
  }

  const schemaId = schemaData.folderItem.tagSchema.id;

  const complexPolymerSchemaConfigurationData = await doQuerySafe<
    ComplexPolymerSchemaConfigurationQuery,
    ComplexPolymerSchemaConfigurationQueryVariables
  >({query: COMPLEX_POLYMER_SCHEMA_CONFIGURATION_QUERY}, {variables: {schemaId}});

  if (
    complexPolymerSchemaConfigurationData.complexPolymerSchemaConfiguration == null ||
    isInaccessible(complexPolymerSchemaConfigurationData.complexPolymerSchemaConfiguration)
  ) {
    return null;
  }

  // If the entity is a complex polymer, populate Redux store with the current version ID
  const complexPolymerVersionData = await doQuerySafe<
    ComplexPolymerCurrentVersionQuery,
    ComplexPolymerCurrentVersionQueryVariables
  >({query: COMPLEX_POLYMER_CURRENT_VERSION_QUERY}, {variables: {complexPolymerId: fileId}});

  if (!isInaccessible(complexPolymerVersionData.complexPolymer)) {
    assert(
      complexPolymerVersionData.complexPolymer.currentVersion != null,
      'Expected complex polymer to have current version'
    );

    getStore().dispatch(
      updateComplexPolymerVersion(fileId, complexPolymerVersionData.complexPolymer.currentVersion.id)
    );
    getStore().dispatch(updateComplexPolymerEditingState(fileId, false));
  }

  return {
    complexPolymerSchemaConfiguration:
      complexPolymerSchemaConfigurationData.complexPolymerSchemaConfiguration,
  };
}
